import { CloseOutlined, SettingOutlined } from '@ant-design/icons'
import { Modal } from 'antd'
import { useContext } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { toast } from 'sonner'

import useSearch from '@/hooks/search/useSearch'
import { ScreenSizeContext } from '@/providers/ScreenSizeProvider'
import { useCartStore } from '@/stores/cart/cartStore'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'

import NameWithTooltip from './NameWithTooltip/NameWithTooltip'
import cn from './OrganisationBar.module.scss'

interface OrganisationBarProps {
  variant?: 'inline' | 'plate'
  accent?: boolean
}

function OrganisationBar({
  variant,
  accent = false,
}: OrganisationBarProps = {}) {
  const isInline = variant === 'inline' || variant === 'plate'
  const isPlate = variant === 'plate'
  const screen = useContext(ScreenSizeContext)
  const location = useLocation()

  const {
    selectedOrganisation,
    setSelectedOrganisation,
    setUserContext,
    setIsOrgSwitching,
    isOrgSwitching,
  } = useUserInfoStore((state) => state)

  const { setProductsQuantity } = useCartStore((state) => state)

  const { setProductInfoToCartPresence } = useFeedStore((state) => state)

  const [modal, contextHolder] = Modal.useModal()

  const {
    searchTerm,
    currentlySearchableCategories,
    currentlySearchableBrands,
    currentlySearchableCharacteristics,
    withLeftovers,
  } = useSearchStore((state) => state)

  const search = useSearch()

  const handleOkConfirm = async () => {
    if (isOrgSwitching) return

    setIsOrgSwitching(true)
    try {
      const response = await api.get('/context')

      const contextData = response.data.context

      localStorage.removeItem('currentClientId')
      localStorage.removeItem('selectedOrganisationCode')
      setUserContext(contextData)
      setSelectedOrganisation(contextData.currentClient || {})

      toast.success('Отмена выбора клиента успешно произведена')

      setProductInfoToCartPresence({})
      setProductsQuantity(0)

      if (location.pathname.includes('/feed')) {
        await search({
          query: searchTerm,
          categories: currentlySearchableCategories,
          brands: currentlySearchableBrands,
          page: 0,
          characteristicFilters: currentlySearchableCharacteristics,
          withLeftovers: withLeftovers,
          clientId: null,
        })
      }
    } catch (error) {
      handleApiError(error, modal)
    } finally {
      setIsOrgSwitching(false)
    }
  }

  if (!Object.keys(selectedOrganisation).length) {
    return null
  }

  return (
    <>
      {contextHolder}
      <div
        className={[
          cn.organisationBar,
          isInline ? cn.organisationBarInline : '',
          isPlate ? cn.organisationBarPlate : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className={cn.nameBlock}>
          {[screen?.lg, screen?.xl].some((value) => value) && (
            <Link to={`/organisations/${selectedOrganisation.code}/settings`}>
              <div
                className={[
                  cn.settingOutlinedIconContainer,
                  isInline ? cn.settingOutlinedIconContainerInline : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <SettingOutlined />
              </div>
            </Link>
          )}
          {isInline ? (
            <span className={cn.orgNameInline}>
              {selectedOrganisation.description}
            </span>
          ) : (
            <NameWithTooltip
              name={selectedOrganisation.description}
              location={accent ? 'organisationBarAccent' : 'organisationBar'}
            />
          )}
          <button
            type="button"
            className={[
              cn.closeOutlinedContainer,
              accent ? cn.closeOutlinedContainerAccent : '',
              isInline ? cn.closeOutlinedContainerInline : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={handleOkConfirm}
            aria-label="Сбросить выбранного клиента"
            disabled={isOrgSwitching}
          >
            <CloseOutlined />
          </button>
        </div>
      </div>
    </>
  )
}

export default OrganisationBar
